import React, { useState } from 'react';
import { X } from 'lucide-react';
import FormularioInforme from './FormularioInforme';
import { useCrearInformeSinPartido } from '@/hooks/useCrearInformeSinPartido';
import { FormularioInforme as FormularioInformeType } from '@/types/informe';
import { Button } from './Button';

interface FormularioInformeModalProps {
  /** Si el modal está abierto */
  isOpen: boolean;
  /** Función para cerrar el modal */
  onClose: () => void;
  /** Función llamada cuando se crea exitosamente */
  onSuccess?: () => void;
}

/**
 * Modal para crear un informe sin partido asignado
 */
export const FormularioInformeModal: React.FC<FormularioInformeModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { crearInforme, isLoading, error } = useCrearInformeSinPartido();
  const [showSuccess, setShowSuccess] = useState(false);

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (formData: FormularioInformeType) => {
    try {
      await crearInforme(formData);
      setShowSuccess(true);
      
      // Mostrar mensaje de éxito por 2 segundos y luego cerrar
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        onSuccess?.();
      }, 2000);
      
    } catch (error) {
      console.error('Error al crear informe:', error);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-secondary-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-700">
          <h2 className="text-xl font-bold text-secondary-50">
            Crear Nuevo Informe
          </h2>
          <Button
            onClick={handleClose}
            variant="secondary"
            size="sm"
            className="p-2"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Contenido */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {showSuccess ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-50 mb-2">
                ¡Informe creado exitosamente!
              </h3>
              <p className="text-secondary-400">
                El informe se ha guardado correctamente
              </p>
            </div>
          ) : (
            <div className="p-4">
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              
              <FormularioInforme
                onSubmit={handleSubmit}
                isLoading={isLoading}
                submitButtonText="Crear Informe"
                sinPartido={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};